import * as moment from 'moment';
import {OptionGroupForRender} from 'src/app/models/ArticleGroup';
import ArticleOption from 'src/app/models/ArticleOption';
import Order from 'src/app/models/Order';
import {OrderChannelsFilter} from 'src/app/models/OrderChannels';
import {OrderPool} from 'src/app/models/OrderPool';
import {OrderStatus} from 'src/app/models/OrderStatus';
import {OrderType} from 'src/app/models/OrderType';
import {PaymentMethod} from 'src/app/models/PaymentMethod';
import {PreorderType} from 'src/app/models/PreorderType';
import {Venue} from 'src/app/models/Venue';

/** Order in which statuses are shown on the manager board. */
export const STATUS_ORDER: (OrderStatus | null)[] = [
	null,
	OrderStatus.AWAITING_CONFIRMATION,
	OrderStatus.IN_PREPARATION,
	OrderStatus.READY,
	OrderStatus.DONE
];

/** Statuses shown when no status filter is selected. */
export const ACTIVE_STATUSES: OrderStatus[] = [
	OrderStatus.AWAITING_CONFIRMATION,
	OrderStatus.IN_PREPARATION,
	OrderStatus.READY
];

export const isActiveStatus = (
	status: OrderStatus | null | undefined
): boolean => status != null && ACTIVE_STATUSES.includes(status);

/**
 * From the start of yesterday through the end of today, so orders placed
 * right before midnight don't disappear once the day rolls over. Used for
 * filtering/counting orders on the board.
 */
export const getOrdersDateRange = () => ({
	from: moment().subtract(1, 'days').startOf('day'),
	to: moment().add(1, 'day').endOf('day')
});

export const isInOrdersDateRange = (date: string | undefined): boolean => {
	const {from, to} = getOrdersDateRange();
	return moment(date).isBetween(from, to, undefined, '[]');
};

const EXTERNAL_ORDER_METHODS: PaymentMethod[] = [
	PaymentMethod.WOLT,
	PaymentMethod.UBER_EATS,
	PaymentMethod.JET
];

export const filterOrderChannel = (
	orders: Order[],
	channels: OrderChannelsFilter
): Order[] => {
	if (channels.length === 0) {
		return orders;
	}

	const channelSet = new Set<OrderType | PaymentMethod>(channels);

	return orders.filter(order => {
		if (EXTERNAL_ORDER_METHODS.includes(order.method)) {
			return channelSet.has(order.method);
		}
		if (order.type === OrderType.TERMINAL || order.type === OrderType.VIRTUAL) {
			return channelSet.has(order.type);
		}
		// Everything else reaches us through the webshop.
		return channelSet.has(OrderType.PREORDER);
	});
};

/**
 * Orders and pools shown on the manager board for the given status filter.
 * Single source of truth for both the board and the status chip counters.
 * Doesn't mutate the input.
 */
export const filterBoardItems = (
	ordersValue: Order[],
	poolsValue: OrderPool[],
	status: OrderStatus | null,
	channels: OrderChannelsFilter
): {orders: Order[]; pools: OrderPool[]} => {
	let orders = ordersValue.filter(
		o =>
			o?.preorder?.type !== PreorderType.FOOD_SPOT &&
			isInOrdersDateRange(o.orderAt)
	);
	let pools = poolsValue;

	if (channels.length > 0) {
		orders = filterOrderChannel(orders, channels);
		// Order pools are their own channel and none of the selectable
		// channels covers them, so an active filter hides them.
		pools = [];
	}

	// ===== ACTIVE MODE =====
	if (!status) {
		return {
			orders: orders.filter(o => isActiveStatus(o.status)),
			pools: pools
				.filter(p => isActiveStatus(p.orderStatus))
				.map(p => ({
					...p,
					orders: p.orders.filter(o => isActiveStatus(o.status))
				}))
		};
	}

	// ===== STATUS MODE =====
	return {
		orders: orders.filter(o => o.status === status),
		pools: pools.filter(p => p.orderStatus === status)
	};
};

export const isWolt = (order: Order): boolean => {
	return order.method === PaymentMethod.WOLT;
};

export const isUber = (order: Order) => {
	return order.method === PaymentMethod.UBER_EATS;
};

export const checkIfOrderPool = (
	value: Order | OrderPool
): value is OrderPool => {
	return (
		(value as OrderPool)?.dndCompany != undefined &&
		(value as OrderPool)?.orders != undefined
	);
};

export const checkIfManualAcceptedView = (
	order: Order | OrderPool,
	venue: Venue | null
) => {
	if (!venue) {
		return false;
	}
	if (checkIfOrderPool(order)) {
		return false;
	}
	if (isWolt(order)) {
		if (
			!venue?.externalOrderProviders?.wolt?.autoAccept &&
			!venue?.externalOrderProviders?.wolt?.autoAcceptByPos &&
			!order.externalOrderAcceptedBy
		) {
			return true;
		}
	}

	if (isUber(order)) {
		if (
			!venue?.externalOrderProviders?.uberEats?.autoAccept &&
			!venue?.externalOrderProviders?.uberEats?.autoAcceptByPos &&
			!order.externalOrderAcceptedBy
		) {
			return true;
		}
	}

	return false;
};

/**
 * Groups the selected options of an article by their option group, keeping
 * the incoming order. Expects options already enriched with the group name
 * in `description` (see ManagerPage.mapOrderOptionGroups).
 */
export const groupOptionsByGroup = (
	options: ArticleOption[] | null | undefined
): OptionGroupForRender[] => {
	const grouped = new Map<string, ArticleOption[]>();
	(options ?? []).forEach(option => {
		const items = grouped.get(option.group);
		if (items) {
			items.push(option);
		} else {
			grouped.set(option.group, [option]);
		}
	});
	return Array.from(grouped, ([groupId, items]) => ({
		groupId,
		groupName: items[0]?.description ?? '',
		items
	}));
};
